import React, { RefObject, useEffect, useState } from 'react';
import { View } from 'react-native';
import { getDocumentTypeName, isIOS, isPhone, isTabletOrWeb, isWeb, navigationRef } from '@helpers';
import { useSelector } from 'react-redux';
import { selectDocumentCategories, updateImageDocumentInDict } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { updateImageMetadata } from '@reducers/v2/imageHandlingV2/thunks';
import { notchTop } from '@components/frame';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { DocTypeButtonProps } from '@v2/components/imageHandling/components/Header/components/DocTypeButton';
import { mapDocumentTypeSvg } from '@v2/components/imageHandling/constants';

export interface UseManageDocTypeButtonParams extends Pick<DocTypeButtonProps, 'images' | 'img' | 'onSuccessDocTypeChange'> {
  docTypeButtonRef?: RefObject<View | null>;
}

export const useManageDocTypeButton = ({
  images,
  img: selectedImageFromProps,
  docTypeButtonRef,
  onSuccessDocTypeChange,
}: UseManageDocTypeButtonParams) => {
  const [docTypeModalOpened, setDocTypeModalOpened] = useState<boolean>(false);
  const route = navigationRef?.current?.getCurrentRoute();

  const dispatch = useAppDispatch();

  const selectedImageKey = route?.params?.selectedImageKey;

  const selectedImageFromParams = images?.find((i) => i?.contentHash === selectedImageKey);

  const selectedImage = selectedImageFromProps ?? selectedImageFromParams;

  const documentCategories = useSelector(selectDocumentCategories);

  const [docTypeButtonMeasurements, setDocTypeButtonMeasurements] = useState<{ x: number; y: number; width: number; height: number }>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  useEffect(() => {
    if (!docTypeButtonRef) return;
    const timeout = setTimeout(() => {
      docTypeButtonRef?.current?.measureInWindow((x, y, width, height) => {
        setDocTypeButtonMeasurements({ x, y, width, height });
      });
    }, 400);

    return () => clearTimeout(timeout);
  }, [docTypeButtonRef]);

  const shouldUseWebIconStyles = isTabletOrWeb;

  const docTypeExist = selectedImage?.documentTag;
  const docTypeButtonFont = shouldUseWebIconStyles ? (!docTypeExist ? 'Button 1' : 'Body') : 'Caption 1';
  const colorWithDocType = shouldUseWebIconStyles ? 'Link/Light/Primary' : 'Label/Light/Tertiary';
  const docTypePaddingHorizontal = shouldUseWebIconStyles ? 13 : !isPhone ? (docTypeExist ? 13 : 10) : 7;
  const docTypeHeight = shouldUseWebIconStyles ? 34 : !isPhone ? 32 : 30;
  const docTypeBorderRadius = shouldUseWebIconStyles ? 18 : !isPhone ? 20 : 10;

  const docTypeCategoriesLoading = !documentCategories.loaded && documentCategories.items.length === 0;
  const imgLoaded = typeof selectedImage === 'object' && !!Object.keys(selectedImage)?.length;

  const docType = getDocumentTypeName({ documentType: selectedImage?.documentTag, documentCategories });

  const svgBasedOnDocType = docType ? mapDocumentTypeSvg[docType] : null;

  const onCloseModal = () => {
    setDocTypeModalOpened(false);
  };

  const onChangeDocType = async (newDocType: string) => {
    if (newDocType === docType) return;
    if (!selectedImage) return;

    // Update documentType on server according to API spec
    const backendData = {
      documentId: +selectedImage.id,
      // documentLinks: selectedImage?.tags
      //   ? selectedImage.tags
      //       .filter((tag) => tag.selected)
      //       .map((tag) => ({
      //         createdDate: null,
      //         isFavorite: tag.favorite || false,
      //         scopeId: tag.scopeId,
      //         scopeReferenceId: tag.scopeReferenceId,
      //       }))
      //   : [],
      documentTags: [newDocType],
    };

    const updatedImageDocument = { ...selectedImage, documentTag: newDocType, documentType: newDocType };

    dispatch(updateImageDocumentInDict({ documentId: selectedImage.documentId, updatedImageDocument }));

    dispatch(
      updateImageMetadata({
        data: backendData,
        ...(onSuccessDocTypeChange ? { onSuccess: () => onSuccessDocTypeChange?.(newDocType, docType) } : {}),
      })
    );
  };

  return {
    docTypeModalOpened,
    docTypeButtonFont,
    colorWithDocType,
    docTypePaddingHorizontal,
    docTypeHeight,
    docTypeBorderRadius,
    selectedImage,
    docTypeCategoriesLoading,
    imgLoaded,
    setDocTypeModalOpened,
    shouldUseWebIconStyles,
    docTypeExist,
    svgBasedOnDocType,
    docType,
    onChangeDocType,
    onCloseModal,
    docTypeButtonMeasurements,
  };
};
